/**
 * DRE Chart Component
 *
 * Multi-series chart that shows selected metrics over time.
 * Supports toggling visibility of each series via selectedMetrics prop.
 * Supports comparison data with side-by-side bars.
 * Includes interactive tooltips with comparison delta display.
 *
 * Styled for Monutrack dark theme with Monumenta Green (#D2FA52) as primary accent.
 */

import { useMemo, useState, useCallback } from 'react';
import { ChartTooltip } from '../../shared/components';
import { usePrivacyStore } from '@/stores/privacyStore';

/** Tooltip data for DRE chart hover */
interface DRETooltipData {
  x: number;
  y: number;
  periodLabel: string;
  metricLabel: string;
  currentValue: number;
  comparisonValue?: number;
}

// Metric definition with color and label
export interface DREMetricConfig {
  key: string;
  label: string;
  color: string;
  type: 'bar' | 'line';
}

// All available DRE metrics - dark theme colors
export const DRE_METRICS: DREMetricConfig[] = [
  { key: 'faturamento', label: 'Faturamento', color: '#D2FA52', type: 'bar' },       // Monumenta Green (primary)
  { key: 'receitaBruta', label: 'Receita Bruta', color: '#a3e635', type: 'bar' },    // lime-400
  { key: 'receitaFinanceira', label: 'Rec. Financeira', color: '#4ade80', type: 'bar' }, // green-400
  { key: 'impostos', label: 'Impostos', color: '#ef4444', type: 'bar' },             // red-500
  { key: 'folha', label: 'Folha', color: '#fb923c', type: 'bar' },                   // orange-400
  { key: 'custosExFolha', label: 'Custos (-Folha)', color: '#f472b6', type: 'bar' }, // pink-400
  { key: 'despesasOperacionais', label: 'Desp. Operac.', color: '#c084fc', type: 'bar' }, // purple-400
  { key: 'lucroLiquido', label: 'Lucro Líquido', color: '#D2FA52', type: 'line' },   // Monumenta Green
];

// Chart data point with all metrics
export interface DREChartDataPoint {
  period: string;
  label: string;
  faturamento: number;
  receitaBruta: number;
  receitaFinanceira: number;
  impostos: number;
  folha: number;
  custosExFolha: number;
  despesasOperacionais: number;
  lucroLiquido: number;
}

export type DREChartType = 'bar' | 'line' | 'combo';

interface DREChartProps {
  data: DREChartDataPoint[];
  comparisonData?: DREChartDataPoint[];
  selectedMetrics: Set<string>;
  /**
   * Optional metric-toggle callback (used by DRE landing). Accepted but currently
   * not consumed inside the chart — caller manages selectedMetrics state. Added
   * to match how `dre/index.tsx:1983` passes the prop. (MONU-74 tech-debt fix.)
   */
  onToggleMetric?: (metricKey: string) => void;
  chartType?: DREChartType;
  height?: number;
}

// Format currency in compact form (Brazilian Real)
function formatCompactCurrency(value: number): string {
  const absValue = Math.abs(value);
  if (absValue >= 1_000_000) {
    return `R$ ${(value / 1_000_000).toFixed(1)}M`;
  }
  if (absValue >= 1_000) {
    return `R$ ${(value / 1_000).toFixed(0)}K`;
  }
  return `R$ ${value.toFixed(0)}`;
}

// Lighten a hex color for comparison bars
function lightenColor(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.min(255, (num >> 16) + amt);
  const G = Math.min(255, ((num >> 8) & 0x00ff) + amt);
  const B = Math.min(255, (num & 0x0000ff) + amt);
  return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
}

export function DREChart({
  data,
  comparisonData,
  selectedMetrics,
  chartType = 'combo',
  height = 280,
}: DREChartProps): JSX.Element {
  const showComparison = !!comparisonData && comparisonData.length > 0;
  const { isSensitiveHidden } = usePrivacyStore();

  // Tooltip state
  const [tooltipData, setTooltipData] = useState<DRETooltipData | null>(null);

  // Stable hover handler
  const handleHover = useCallback((data: DRETooltipData | null) => {
    setTooltipData(data);
  }, []);

  // Filter to only selected metrics
  const activeMetrics = useMemo(() => {
    return DRE_METRICS.filter(m => selectedMetrics.has(m.key));
  }, [selectedMetrics]);

  // Determine which metrics render as bars vs lines based on chartType
  const { barMetrics, lineMetrics } = useMemo(() => {
    if (chartType === 'bar') {
      return { barMetrics: activeMetrics, lineMetrics: [] };
    } else if (chartType === 'line') {
      return { barMetrics: [], lineMetrics: activeMetrics };
    } else {
      return {
        barMetrics: activeMetrics.filter(m => m.type === 'bar'),
        lineMetrics: activeMetrics.filter(m => m.type === 'line'),
      };
    }
  }, [activeMetrics, chartType]);

  const chartConfig = useMemo(() => {
    if (data.length === 0 || activeMetrics.length === 0) {
      return null;
    }

    // Calculate max value for scaling across all selected metrics (including comparison)
    const allValues = data.flatMap(d =>
      activeMetrics.map(m => Math.abs(d[m.key as keyof DREChartDataPoint] as number))
    );
    if (comparisonData) {
      comparisonData.forEach(d => {
        activeMetrics.forEach(m => {
          allValues.push(Math.abs(d[m.key as keyof DREChartDataPoint] as number));
        });
      });
    }
    const maxValue = Math.max(...allValues, 1);

    return { maxValue };
  }, [data, comparisonData, activeMetrics]);

  if (!chartConfig || data.length === 0 || activeMetrics.length === 0) {
    return (
      <div style={{
        padding: '2rem',
        textAlign: 'center',
        color: '#9ca3af',
        background: '#1f2937',
        borderRadius: '8px',
      }}>
        Selecione métricas para exibir no gráfico
      </div>
    );
  }

  const { maxValue } = chartConfig;

  // Chart dimensions - wider bars when comparison is shown
  const paddingLeft = 70;
  const paddingRight = 150;
  const paddingTop = 20;
  const paddingBottom = 50;

  // Calculate bar widths based on whether we have comparison
  const barsPerGroup = showComparison ? barMetrics.length * 2 : barMetrics.length;
  const singleBarWidth = Math.min(12, 80 / Math.max(barsPerGroup, 1));
  const barGroupWidth = Math.max(barsPerGroup * (singleBarWidth + 2) + 20, 60);
  const chartWidth = Math.max(data.length * (barGroupWidth + 10) + paddingLeft + paddingRight, 600);
  const chartHeight = height - paddingTop - paddingBottom;

  const xStep = (chartWidth - paddingLeft - paddingRight) / data.length;
  const getX = (i: number): number => paddingLeft + i * xStep + xStep / 2;
  const getY = (val: number): number => paddingTop + chartHeight - (Math.abs(val) / maxValue) * chartHeight;

  // Generate line path
  const generateLinePath = (metricKey: string, dataSet: DREChartDataPoint[]): string => {
    const points = dataSet.map((d, i) => {
      const value = d[metricKey as keyof DREChartDataPoint] as number;
      return `${getX(i)},${getY(value)}`;
    });
    return `M ${points.join(' L ')}`;
  };

  return (
    <div style={{
      background: '#1f2937',
      borderRadius: '12px',
      padding: '1rem',
      overflowX: 'auto',
      position: 'relative',
    }}>
      <svg width={chartWidth} height={height} style={{ display: 'block' }}>
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((pct, i) => (
          <g key={i}>
            <line
              x1={paddingLeft}
              y1={paddingTop + chartHeight * (1 - pct)}
              x2={chartWidth - paddingRight}
              y2={paddingTop + chartHeight * (1 - pct)}
              stroke="#374151"
              strokeDasharray={pct === 0 ? '' : '4'}
            />
            <text
              x={paddingLeft - 8}
              y={paddingTop + chartHeight * (1 - pct) + 4}
              textAnchor="end"
              fontSize="11"
              fill="#9ca3af"
            >
              {isSensitiveHidden ? 'R$ ••••••' : formatCompactCurrency(maxValue * pct)}
            </text>
          </g>
        ))}

        {/* Bars for each period */}
        {data.map((d, periodIdx) => {
          const x = getX(periodIdx);
          const barBottom = paddingTop + chartHeight;
          const compD = comparisonData?.[periodIdx];

          // Calculate total width needed for all bars
          const totalBars = showComparison ? barMetrics.length * 2 : barMetrics.length;
          const totalBarWidth = totalBars * singleBarWidth + (totalBars - 1) * 2;
          const startX = x - totalBarWidth / 2;

          return (
            <g key={d.period}>
              {barMetrics.map((metric, metricIdx) => {
                const value = Math.abs(d[metric.key as keyof DREChartDataPoint] as number);
                const barHeight = (value / maxValue) * chartHeight;

                if (showComparison && compD) {
                  // Side-by-side bars: current + comparison
                  const compValue = Math.abs(compD[metric.key as keyof DREChartDataPoint] as number);
                  const compBarHeight = (compValue / maxValue) * chartHeight;
                  const pairIdx = metricIdx * 2;
                  const currentBarX = startX + pairIdx * (singleBarWidth + 2);
                  const compBarX = startX + (pairIdx + 1) * (singleBarWidth + 2);
                  const barY = barBottom - Math.max(barHeight, compBarHeight);

                  const handleMouseEnter = () => {
                    handleHover({
                      x: x,
                      y: barY - 8,
                      periodLabel: d.label,
                      metricLabel: metric.label,
                      currentValue: value,
                      comparisonValue: compValue,
                    });
                  };

                  const handleMouseLeave = () => {
                    handleHover(null);
                  };

                  return (
                    <g key={`${d.period}-${metric.key}`}>
                      {/* Current period bar */}
                      <rect
                        x={currentBarX}
                        y={barBottom - barHeight}
                        width={singleBarWidth}
                        height={Math.max(barHeight, 1)}
                        fill={metric.color}
                        rx="2"
                        opacity={0.9}
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                        onFocus={handleMouseEnter}
                        onBlur={handleMouseLeave}
                        tabIndex={0}
                        role="graphics-symbol"
                        aria-label={`${d.label} ${metric.label}: ${isSensitiveHidden ? 'R$ ••••••' : formatCompactCurrency(value)}`}
                      />
                      {/* Comparison period bar (lighter) */}
                      <rect
                        x={compBarX}
                        y={barBottom - compBarHeight}
                        width={singleBarWidth}
                        height={Math.max(compBarHeight, 1)}
                        fill={lightenColor(metric.color, 40)}
                        rx="2"
                        opacity={0.6}
                        strokeDasharray="2"
                        stroke={metric.color}
                        strokeWidth="0.5"
                        style={{ cursor: 'pointer' }}
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                      />
                    </g>
                  );
                } else {
                  // Single bars (no comparison)
                  const barX = startX + metricIdx * (singleBarWidth + 2);
                  const barY = barBottom - barHeight;

                  const handleMouseEnter = () => {
                    handleHover({
                      x: barX + singleBarWidth / 2,
                      y: barY - 8,
                      periodLabel: d.label,
                      metricLabel: metric.label,
                      currentValue: value,
                    });
                  };

                  const handleMouseLeave = () => {
                    handleHover(null);
                  };

                  return (
                    <rect
                      key={`${d.period}-${metric.key}`}
                      x={barX}
                      y={barY}
                      width={singleBarWidth}
                      height={Math.max(barHeight, 1)}
                      fill={metric.color}
                      rx="2"
                      opacity={0.85}
                      style={{ cursor: 'pointer' }}
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                      onFocus={handleMouseEnter}
                      onBlur={handleMouseLeave}
                      tabIndex={0}
                      role="graphics-symbol"
                      aria-label={`${d.label} ${metric.label}: ${isSensitiveHidden ? 'R$ ••••••' : formatCompactCurrency(value)}`}
                    />
                  );
                }
              })}
            </g>
          );
        })}

        {/* Lines for current data */}
        {lineMetrics.map(metric => (
          <g key={metric.key}>
            <path
              d={generateLinePath(metric.key, data)}
              fill="none"
              stroke={metric.color}
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {data.map((d, i) => {
              const value = d[metric.key as keyof DREChartDataPoint] as number;
              const cx = getX(i);
              const cy = getY(value);
              const compD = comparisonData?.[i];
              const compValue = compD ? (compD[metric.key as keyof DREChartDataPoint] as number) : undefined;

              const handleMouseEnter = () => {
                handleHover({
                  x: cx,
                  y: cy - 12,
                  periodLabel: d.label,
                  metricLabel: metric.label,
                  currentValue: value,
                  comparisonValue: showComparison ? compValue : undefined,
                });
              };

              const handleMouseLeave = () => {
                handleHover(null);
              };

              return (
                <g key={`${metric.key}-${d.period}`}>
                  <circle
                    cx={cx}
                    cy={cy}
                    r="5"
                    fill={metric.color}
                    stroke="#1f2937"
                    strokeWidth="2"
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onFocus={handleMouseEnter}
                    onBlur={handleMouseLeave}
                    tabIndex={0}
                    role="graphics-symbol"
                    aria-label={`${d.label} ${metric.label}: ${isSensitiveHidden ? 'R$ ••••••' : formatCompactCurrency(value)}`}
                  />
                  <text
                    x={cx}
                    y={cy - 10}
                    textAnchor="middle"
                    fontSize="9"
                    fill={metric.color}
                    fontWeight="600"
                    style={{ pointerEvents: 'none' }}
                  >
                    {isSensitiveHidden ? 'R$ ••••••' : formatCompactCurrency(value)}
                  </text>
                </g>
              );
            })}
          </g>
        ))}

        {/* Lines for comparison data (dashed) */}
        {showComparison && comparisonData && lineMetrics.map(metric => (
          <g key={`comp-${metric.key}`}>
            <path
              d={generateLinePath(metric.key, comparisonData)}
              fill="none"
              stroke={lightenColor(metric.color, 30)}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeDasharray="6,3"
            />
            {comparisonData.map((d, i) => {
              const value = d[metric.key as keyof DREChartDataPoint] as number;
              return (
                <circle
                  key={`comp-${metric.key}-${d.period}`}
                  cx={getX(i)}
                  cy={getY(value)}
                  r="4"
                  fill={lightenColor(metric.color, 30)}
                  stroke="#1f2937"
                  strokeWidth="1"
                />
              );
            })}
          </g>
        ))}

        {/* X-axis labels */}
        {data.map((d, i) => (
          <text
            key={`label-${d.period}`}
            x={getX(i)}
            y={height - 15}
            textAnchor="middle"
            fontSize="11"
            fill="#9ca3af"
          >
            {d.label}
          </text>
        ))}

        {/* Legend */}
        {activeMetrics.map((metric, i) => {
          const legendY = paddingTop + i * 18;
          const isLine = chartType === 'line' || (chartType === 'combo' && metric.type === 'line');

          return (
            <g key={metric.key}>
              {isLine ? (
                <>
                  <line
                    x1={chartWidth - 140}
                    y1={legendY + 6}
                    x2={chartWidth - 128}
                    y2={legendY + 6}
                    stroke={metric.color}
                    strokeWidth="3"
                  />
                  <circle cx={chartWidth - 134} cy={legendY + 6} r="3" fill={metric.color} />
                </>
              ) : (
                <>
                  <rect
                    x={chartWidth - 140}
                    y={legendY}
                    width={showComparison ? 6 : 12}
                    height={12}
                    fill={metric.color}
                    rx="2"
                  />
                  {showComparison && (
                    <rect
                      x={chartWidth - 133}
                      y={legendY}
                      width={6}
                      height={12}
                      fill={lightenColor(metric.color, 40)}
                      rx="2"
                      opacity={0.6}
                    />
                  )}
                </>
              )}
              <text
                x={chartWidth - 122}
                y={legendY + 10}
                fontSize="10"
                fill="#f3f4f6"
              >
                {metric.label}
              </text>
            </g>
          );
        })}

        {/* Comparison legend indicator */}
        {showComparison && (
          <g>
            <rect
              x={chartWidth - 140}
              y={paddingTop + activeMetrics.length * 18 + 8}
              width={18}
              height={1}
              fill="#6b7280"
            />
            <text
              x={chartWidth - 118}
              y={paddingTop + activeMetrics.length * 18 + 12}
              fontSize="9"
              fill="#9ca3af"
              fontStyle="italic"
            >
              Comparação
            </text>
          </g>
        )}
      </svg>

      {/* Interactive tooltip */}
      {tooltipData && (
        <ChartTooltip
          visible={true}
          x={tooltipData.x}
          y={tooltipData.y}
          periodLabel={`${tooltipData.periodLabel} - ${tooltipData.metricLabel}`}
          currentValue={tooltipData.currentValue}
          comparisonValue={tooltipData.comparisonValue}
          comparisonPeriodLabel={showComparison ? 'Período anterior' : undefined}
          isCurrency={true}
        />
      )}
    </div>
  );
}

export default DREChart;
